(() => {
  const root = document.documentElement;

  const mobileHeaderQuery = window.matchMedia('(max-width: 1022px)');
  let compactStart = 80;
  let compactSnap = 120;
  let measureFrame = null;
  let isHeaderScrollCompensating = false;

  const getHeaderFlowElement = () => {
    const candidates = [
      document.querySelector('.site-header-wrapper'),
      document.querySelector('[data-section-type="static-header"]'),
      document.querySelector('.site-header'),
    ].filter(Boolean);

    return candidates.find((element) => {
      const rect = element.getBoundingClientRect();
      return getComputedStyle(element).display !== 'contents' && rect.height > 0;
    }) || candidates.at(-1) || null;
  };

  const getHeaderSnapAnchor = () =>
    document.querySelector(
      '.product-main, .product--outer, [data-section-type="static-product"], .productgrid--outer, #MainContent, main',
    );

  const updateHeaderState = () => {
    if (!mobileHeaderQuery.matches) {
      root.classList.remove('cm-header-controls-exiting', 'cm-header-scrolled');
      return;
    }

    const isExiting = window.scrollY >= compactStart && window.scrollY < compactSnap;
    const isCompact = window.scrollY >= compactSnap;
    const wasCompact = root.classList.contains('cm-header-scrolled');
    const shouldCompensate = wasCompact !== isCompact && !isHeaderScrollCompensating;
    const anchor = shouldCompensate ? getHeaderSnapAnchor() : null;
    const beforeTop = anchor?.getBoundingClientRect().top;

    root.classList.toggle('cm-header-controls-exiting', isExiting);
    root.classList.toggle('cm-header-scrolled', isCompact);

    if (anchor && Number.isFinite(beforeTop)) {
      const afterTop = anchor.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;

      if (Math.abs(delta) > 1) {
        isHeaderScrollCompensating = true;
        window.scrollBy(0, delta);

        window.requestAnimationFrame(() => {
          isHeaderScrollCompensating = false;
        });
      }
    }
  };

  const measureHeaderScrollPoints = () => {
    if (!mobileHeaderQuery.matches) {
      compactStart = Number.POSITIVE_INFINITY;
      compactSnap = Number.POSITIVE_INFINITY;
      updateHeaderState();
      return;
    }

    const hadExiting = root.classList.contains('cm-header-controls-exiting');
    const hadCompact = root.classList.contains('cm-header-scrolled');

    root.classList.remove('cm-header-controls-exiting', 'cm-header-scrolled');

    const header = getHeaderFlowElement();
    const controlRow = document.querySelector('#cm-mobile-garage') || document.querySelector('.live-search');
    const headerRect = header?.getBoundingClientRect();
    const rect = controlRow?.getBoundingClientRect();

    if (headerRect) {
      root.style.setProperty('--cm-mobile-header-flow-height', `${Math.round(headerRect.height)}px`);
    }

    if (rect) {
      compactSnap = Math.max(0, Math.round(window.scrollY + rect.top));
      compactStart = Math.max(0, Math.round(window.scrollY + (headerRect?.top ?? rect.top - 80)));

      if (compactStart >= compactSnap) {
        compactStart = Math.max(0, compactSnap - 1);
      }
    } else {
      compactStart = 80;
      compactSnap = 120;
    }

    root.classList.toggle('cm-header-controls-exiting', hadExiting);
    root.classList.toggle('cm-header-scrolled', hadCompact);
    updateHeaderState();
  };

  const scheduleHeaderMeasure = () => {
    if (measureFrame) return;

    measureFrame = window.requestAnimationFrame(() => {
      measureFrame = null;
      measureHeaderScrollPoints();
    });
  };

  scheduleHeaderMeasure();
  window.addEventListener('load', scheduleHeaderMeasure);
  window.addEventListener('scroll', updateHeaderState, { passive: true });
  window.addEventListener('resize', scheduleHeaderMeasure);
  mobileHeaderQuery.addEventListener?.('change', scheduleHeaderMeasure);
  window.setTimeout(scheduleHeaderMeasure, 500);
  window.setTimeout(scheduleHeaderMeasure, 1500);

  const updateSearchPlaceholders = () => {
    document
      .querySelectorAll('.cm_searchInput, .live-search input, input[type="search"], input[name="q"]')
      .forEach((input) => {
        if (input.matches('[data-parity-product-type-v3-search]')) return;
        input.setAttribute('placeholder', 'Search');
        input.setAttribute('aria-label', 'Search');
      });
  };

  const updateGarageLabels = () => {
    document
      .querySelectorAll('#cm-garage [title], #cm-mobile-garage [title]')
      .forEach((element) => {
        element.removeAttribute('title');
      });
  };

  const normalizeMeteorNavText = (value) =>
    String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const normalizeMeteorNavHandle = (value) =>
    normalizeMeteorNavText(value)
      .replace(/&/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const createMeteorCategoryMap = (entries) =>
    new Map(
      entries.flatMap(([targetUrl, labels]) =>
        labels.map((label) => [normalizeMeteorNavHandle(label), targetUrl]),
      ),
    );

  const meteorHeaderCategoryLinksByContext = {
    motorcycleParts: createMeteorCategoryMap([
      ['/collections/motorcycle-parts', ['catalog', 'parts', 'motorcycle parts', 'moto parts', 'parts & accessories', 'motorcycle parts & accessories', 'shop all', 'shop all parts', 'shop all motorcycle parts', 'shop by category']],
      ['/collections/motorcycle-accessories', ['accessories', 'accessories-2', 'all accessories', 'motorcycle accessories', 'moto accessories', 'moto access', 'motorcycle access', 'audio communication & mounts', 'hardware covers & general']],
      ['/collections/utv-atv-parts', ['utv/atv', 'utv / atv', 'utv atv', 'utv-atv', 'utv parts', 'atv parts', 'utv/atv parts']],
      ['/collections/tires', ['tires', 'motorcycle tires', 'moto tires']],
      ['/collections/motorcycle-air-cleaners', ['air cleaners']],
      ['/collections/motorcycle-audio', ['audio & speakers', 'motorcycle audio']],
      ['/collections/motorcycle-batteries-electrical', ['batteries & electrical', 'electrical']],
      ['/collections/motorcycle-bike-protection', ['bike protection']],
      ['/collections/motorcycle-brakes', ['brakes']],
      ['/collections/motorcycle-body-parts', ['body, fairing & fender', 'fenders & license plate frames']],
      ['/collections/motorcycle-dash-gauges', ['dashes & gauges', 'dash & gauges']],
      ['/collections/motorcycle-drive-transmission', ['drive & transmission', 'transmission & driveline']],
      ['/collections/motorcycle-engine', ['engine']],
      ['/collections/motorcycle-exhaust', ['exhaust']],
      ['/collections/motorcycle-air-fuel-oil-filters', ['filters']],
      ['/collections/motorcycle-fenders-extenders', ['fenders']],
      ['/collections/motorcycle-foot-controls', ['footpegs & foot controls', 'footrests & floorboards', 'foot controls']],
      ['/collections/motorcycle-air-fuel', ['fuel & air systems', 'air & fuel']],
      ['/collections/motorcycle-fuel-management', ['fuel management']],
      ['/collections/motorcycle-gaskets', ['gaskets & seals']],
      ['/collections/motorcycle-hand-controls-handlebars', ['handlebars controls & mirrors', 'handlebars & controls']],
      ['/collections/motorcycle-handlebars', ['handlebars']],
      ['/collections/motorcycle-lighting', ['lighting']],
      ['/collections/motorcycle-mirrors', ['mirrors & accessories']],
      ['/collections/motorcycle-oil-filters', ['oil filters & gauges']],
      ['/collections/motorcycle-gas-oil-tanks', ['tanks & oil filters', 'gas & oil tanks']],
      ['/collections/motorcycle-suspension', ['suspension', 'frames & suspension', 'suspension & frame']],
      ['/collections/motorcycle-wheel-axle', ['wheels', 'wheel & axle', 'wheels & axles']],
      ['/collections/motorcycle-windshields-windscreens', ['windshields & fairings', 'windshields & windscreens']],
      ['/collections/motorcycle-seating', ['seats & sissy bars']],
      ['/collections/motorcycle-tools', ['service & maintenance tools']],
      ['/collections/motorcycle-luggage-and-bags', ['saddlebags & luggage']],
      ['/collections/motorcycle-transport-storage', ['transport & trailer lock']],
    ]),
    motorcycleAccessories: createMeteorCategoryMap([
      ['/collections/motorcycle-accessories', ['accessories', 'accessories-2', 'all accessories', 'motorcycle accessories', 'moto accessories', 'moto access', 'motorcycle access', 'shop all', 'shop all accessories', 'audio communication & mounts', 'hardware covers & general']],
      ['/collections/motorcycle-tools', ['tools', 'service & maintenance tools']],
    ]),
    utvAtvParts: createMeteorCategoryMap([
      ['/collections/utv-atv-parts', ['utv/atv', 'utv / atv', 'utv atv', 'utv-atv', 'utv/atv parts', 'utv parts', 'atv parts', 'shop all', 'shop all utv atv parts']],
      ['/collections/utv-atv-accessories', ['accessories', 'all accessories', 'utv/atv accessories', 'utv accessories', 'atv accessories']],
      ['/collections/utv-atv-tires', ['tires']],
      ['/collections/utv-atv-tools', ['tools']],
    ]),
    utvAtvAccessories: createMeteorCategoryMap([
      ['/collections/utv-atv-accessories', ['utv/atv accessories', 'utv / atv accessories', 'utv accessories', 'atv accessories', 'accessories', 'all accessories', 'shop all']],
      ['/collections/utv-atv-parts', ['parts', 'utv/atv', 'utv / atv', 'utv atv', 'utv-atv', 'utv/atv parts']],
      ['/collections/utv-atv-tools', ['tools']],
    ]),
    ridingGear: createMeteorCategoryMap([
      ['/collections/riding-gear', ['riding gear', 'gear', 'motorcycle gear', 'moto gear', 'apparel', 'shop all', 'shop all riding gear']],
    ]),
    tires: createMeteorCategoryMap([
      ['/collections/tires', ['tires', 'motorcycle tires', 'moto tires', 'shop all', 'shop all tires']],
      ['/collections/utv-atv-tires', ['utv/atv', 'utv / atv', 'utv atv', 'utv/atv tires', 'utv / atv tires', 'utv-atv tires']],
    ]),
  };

  const meteorHeaderRootHandlesByContext = {
    motorcycleParts: new Set(['catalog', 'parts', 'motorcycle-parts', 'moto-parts', 'parts-accessories', 'motorcycle-parts-accessories', 'shop-all-parts', 'shop-all-motorcycle-parts', 'shop-by-category']),
    motorcycleAccessories: new Set(['accessories', 'accessories-2', 'all-accessories', 'motorcycle-accessories', 'moto-accessories', 'moto-access', 'motorcycle-access', 'shop-all-accessories']),
    utvAtvParts: new Set(['utv-atv', 'utv-atv-parts', 'utv-parts', 'atv-parts', 'utv-and-atv-parts']),
    utvAtvAccessories: new Set(['utv-atv-accessories', 'utv-accessories', 'atv-accessories', 'utv-atv-access', 'utv-access', 'atv-access']),
    ridingGear: new Set(['riding-gear', 'gear', 'motorcycle-gear', 'moto-gear', 'apparel', 'riding-gear-apparel']),
    tires: new Set(['tires', 'motorcycle-tires', 'moto-tires', 'shop-all-tires']),
  };

  const rootItemContextOrder = ['utvAtvAccessories', 'utvAtvParts', 'motorcycleAccessories', 'ridingGear', 'tires', 'motorcycleParts'];
  const menuContextOrder = ['motorcycleParts', 'utvAtvAccessories', 'utvAtvParts', 'ridingGear', 'tires'];

  const getDirectMeteorNavLink = (item) =>
    Array.from(item?.children || []).find((child) =>
      child.matches?.('a[href], button, [role="button"], [data-href]'),
    ) || item?.querySelector?.('a[href], button, [role="button"], [data-href]');

  const getMeteorContextForRootLabel = (value, contextOrder = rootItemContextOrder) => {
    const handle = normalizeMeteorNavHandle(value);

    return contextOrder.find((context) => meteorHeaderRootHandlesByContext[context]?.has(handle)) || null;
  };

  const rewriteMeteorNavAnchor = (anchor, forcedLabel, context = 'motorcycleParts') => {
    if (!anchor?.matches?.('a[href]')) return;

    const normalizedLabel = normalizeMeteorNavHandle(forcedLabel || anchor.textContent);
    const targetUrl = meteorHeaderCategoryLinksByContext[context]?.get(normalizedLabel);

    if (targetUrl && anchor.getAttribute('href') !== targetUrl) {
      anchor.setAttribute('href', targetUrl);
    }
  };

  const getMeteorDesktopMenuContext = (menu) => {
    const labelledGroups = Array.from(menu?.querySelectorAll?.('[role="group"][aria-label], .m-nav[aria-label]') || []);
    const groupHandles = new Set(labelledGroups.map((group) => normalizeMeteorNavHandle(group.getAttribute('aria-label'))));

    return menuContextOrder.find((context) =>
      Array.from(meteorHeaderRootHandlesByContext[context] || []).some((handle) => groupHandles.has(handle)),
    ) || null;
  };

  const updateMeteorHeaderNavigation = () => {
    const nav = document.querySelector('#meteor-desktop-nav');

    if (nav) {
      const topLevelItems = Array.from(nav.children).filter((child) => child.matches?.('li'));
      let normalizedAnyRoot = false;

      topLevelItems.forEach((item) => {
        const directLink = getDirectMeteorNavLink(item);
        const context = getMeteorContextForRootLabel(directLink?.textContent);

        if (!context) return;

        normalizedAnyRoot = true;
        rewriteMeteorNavAnchor(directLink, directLink?.textContent, context);
        item.querySelectorAll('a[href]').forEach((anchor) => rewriteMeteorNavAnchor(anchor, null, context));
      });

      if (!normalizedAnyRoot && topLevelItems[3]) {
        const fallbackPartsItem = topLevelItems[3];
        rewriteMeteorNavAnchor(getDirectMeteorNavLink(fallbackPartsItem), 'parts', 'motorcycleParts');
        fallbackPartsItem.querySelectorAll('a[href]').forEach((anchor) => rewriteMeteorNavAnchor(anchor, null, 'motorcycleParts'));
      }
    }

    document.querySelectorAll('.Meteor-MobileMenu[aria-label], .Meteor-Drawer__ItemList[aria-label], [role="menu"][aria-label]').forEach((menu) => {
      const context = getMeteorContextForRootLabel(menu.getAttribute('aria-label'));

      if (!context) return;

      menu.querySelectorAll('a[href]').forEach((anchor) => rewriteMeteorNavAnchor(anchor, null, context));
    });

    document
      .querySelectorAll('.Meteor-MobileNavigation__Link > a[href], a.Meteor-Navigation__Link__mobile[href]')
      .forEach((anchor) => {
        const context = getMeteorContextForRootLabel(anchor.textContent);

        if (!context) return;

        rewriteMeteorNavAnchor(anchor, anchor.textContent, context);
      });

    document.querySelectorAll('.meteor-menu').forEach((menu) => {
      const context = getMeteorDesktopMenuContext(menu);

      if (!context) return;

      menu.querySelectorAll('a[href]').forEach((anchor) => rewriteMeteorNavAnchor(anchor, null, context));
    });
  };

  const updateConvermaxHeaderControls = () => {
    updateSearchPlaceholders();
    updateGarageLabels();
    updateMeteorHeaderNavigation();
  };

  updateConvermaxHeaderControls();
  document.addEventListener('DOMContentLoaded', updateConvermaxHeaderControls);

  if ('MutationObserver' in window) {
    new MutationObserver(updateConvermaxHeaderControls).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();

window.parityGarageRideCycleStates = [
  { id: 'harley-touring', label: 'Harley Touring' },
  { id: 'harley-sportster', label: 'Harley Sportster' },
  { id: 'harley-trike', label: 'Harley Trike' },
  { id: 'harley-cruiser', label: 'Harley Cruiser' },
  { id: 'dirtbike', label: 'Dirtbike' },
  { id: 'utv', label: 'UTV' },
  { id: 'atv', label: 'ATV' },
];
